/* RewardedAdModal — watch partner ads to unlock more free plays today.
 *
 * 2026-06 redesign: AdSense sniffing removed. Each "ad" is a Play Tsoro
 * partner creative (self-hosted, always fills) shown for 10 seconds, then
 * POST /api/ads/ad-view {ad_status: 'filled'}. The server resets
 * plays_remaining after ADS_REQUIRED_TO_UNLOCK completed views.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Play, X } from 'lucide-react';

const AD_SECONDS = 10;
const PARTNER_URL = 'https://www.playtsoro.com';
const CREATIVES = [
    { key: 'plant-a-seed', img: '/brand/tsoro_portrait_plant_a_seed.png', alt: 'Play Tsoro — Plant a seed. Plan a move.' },
    { key: 'teach-strategy', img: '/brand/tsoro_portrait_teach_strategy.png', alt: 'Play Tsoro — Teach strategy through play.' },
    { key: 'continent-of-cousins', img: '/brand/tsoro_portrait_continent_of_cousins.png', alt: 'Play Tsoro — One game, a continent of cousins.' },
];

export default function RewardedAdModal({
    puzzleType, languageSlug, puzzleName, adsRequired = 3,
    adsAlreadyWatched = 0, onUnlocked, onClose,
}) {
    const [adsDone, setAdsDone] = useState(adsAlreadyWatched);
    const [secondsLeft, setSecondsLeft] = useState(AD_SECONDS);
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const creative = CREATIVES[adsDone % CREATIVES.length];

    useEffect(() => {
        if (!isPlaying) return undefined;
        if (secondsLeft <= 0) {
            (async () => {
                try {
                    const { data } = await api.post('/ads/ad-view', {
                        puzzle_type: puzzleType,
                        language_slug: languageSlug,
                        ad_status: 'filled',
                    });
                    if (data.unlocked) {
                        if (onUnlocked) onUnlocked(data);
                        return;
                    }
                    setAdsDone(data.ads_watched_in_cycle);
                    setIsPlaying(false);
                    setSecondsLeft(AD_SECONDS);
                } catch {
                    setError('Could not record ad view. Please try again.');
                    setIsPlaying(false);
                    setSecondsLeft(AD_SECONDS);
                }
            })();
            return undefined;
        }
        const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [isPlaying, secondsLeft, puzzleType, languageSlug, onUnlocked]);

    const start = () => { setSecondsLeft(AD_SECONDS); setError(null); setIsPlaying(true); };
    const pct = Math.round(((AD_SECONDS - secondsLeft) / AD_SECONDS) * 100);

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" data-testid="rewarded-ad-modal">
            <div className="relative bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl my-8">
                <button onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-lq-ink-100 hover:bg-lq-ink-300 inline-flex items-center justify-center text-lq-ink-700" aria-label="Close" data-testid="rewarded-ad-close">
                    <X size={16}/>
                </button>
                <div className="px-6 pt-6 pb-3">
                    <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Unlock more plays</div>
                    <h2 className="mt-1 font-display text-2xl font-semibold text-lq-ink-900">Watch {adsRequired} short ads for 3 more plays today</h2>
                    <p className="mt-1 text-sm text-lq-ink-500">Or skip ads forever &mdash; <Link to="/pricing" className="text-lq-purple-700 hover:underline">plans from &pound;3.49/mo</Link>.</p>
                </div>

                <div className="px-6 flex items-center gap-2">
                    {Array.from({ length: adsRequired }).map((_, i) => (
                        <div key={i} className={`h-2 flex-1 rounded-full ${i < adsDone ? 'bg-lq-success' : 'bg-lq-ink-100'}`} data-testid={`rewarded-ad-dot-${i}`} />
                    ))}
                </div>

                <div className="px-6 pt-4 pb-6">
                    {!isPlaying && (
                        <>
                            <div className="bg-lq-paper border-2 border-dashed border-lq-ink-300 rounded-xl p-5 text-center text-sm text-lq-ink-500" data-testid="rewarded-ad-intro">
                                A short advert from a family-safe partner plays for {AD_SECONDS} seconds. Paid plans skip this step entirely.
                            </div>
                            <button onClick={start}
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold py-3 rounded-full"
                                data-testid="rewarded-ad-play">
                                <Play size={16}/> Play ad {adsDone + 1} of {adsRequired}
                            </button>
                        </>
                    )}

                    {isPlaying && (
                        <div className="rounded-2xl overflow-hidden border border-lq-ink-100" data-testid="rewarded-ad-playing" style={{ background: '#f7f1e8' }}>
                            <div className="px-4 pt-3 flex items-center justify-between text-[11px] uppercase tracking-wider text-lq-ink-500">
                                <span>Ad &middot; playtsoro.com</span>
                                <span className="font-display text-base text-lq-ink-900" data-testid="rewarded-ad-timer">{secondsLeft}s</span>
                            </div>
                            <div className="px-4 pt-1"><div className="h-1.5 rounded-full bg-black/10 overflow-hidden"><div className="h-full bg-lq-purple-500 transition-all" style={{ width: `${pct}%` }} /></div></div>
                            <a href={PARTNER_URL} target="_blank" rel="noopener noreferrer sponsored"
                                onClick={() => api.post('/ads/placements/puzzle-rewarded/click').catch(() => {})}
                                className="block" data-testid="rewarded-ad-creative-link">
                                <img key={creative.key} src={creative.img} alt={creative.alt} className="w-full h-auto max-h-[380px] object-contain mx-auto block" />
                            </a>
                            <p className="px-4 pb-3 text-xs text-lq-ink-700 text-center">{puzzleName} unlocks after {adsRequired - adsDone} more {adsRequired - adsDone === 1 ? 'ad' : 'ads'}.</p>
                        </div>
                    )}

                    {error && <p className="mt-3 text-sm text-lq-error" data-testid="rewarded-ad-error">{error}</p>}
                </div>
            </div>
        </div>
    );
}
